import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { LayoutDashboard, UserCircle, MessageCircle, DollarSign, MoreHorizontal, CheckSquare, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';

const items = [
  { label: 'Inicio', icon: LayoutDashboard, to: '/m/' },
  { label: 'Contatos', icon: UserCircle, to: '/m/contacts' },
  { label: 'Mensagens', icon: MessageCircle, to: '/m/whatsapp' },
  { label: 'Negocios', icon: DollarSign, to: '/m/deals' },
];

const prefetchMap: Record<string, () => Promise<unknown>> = {
  '/m/': () => import('@/pages/mobile/MobileHome'),
  '/m/contacts': () => import('@/pages/mobile/MobileContacts'),
  '/m/whatsapp': () => import('@/pages/mobile/MobileWhatsApp'),
  '/m/deals': () => import('@/pages/mobile/MobileDeals'),
  '/m/tasks': () => import('@/pages/mobile/MobileTasks'),
  '/m/calls': () => import('@/pages/mobile/MobileCalls'),
  '/m/profile': () => import('@/pages/mobile/MobileProfile'),
};

function prefetchRoute(route: string) {
  prefetchMap[route]?.();
}

export function MobileBottomNavV2() {
  const [mounted, setMounted] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const moreActive = (
    location.pathname.startsWith('/m/tasks') ||
    location.pathname.startsWith('/m/calls') ||
    location.pathname.startsWith('/m/profile')
  );

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/85 h-[var(--bottom-nav-total-height)] pb-[env(safe-area-inset-bottom)]">
      <div className="flex h-[var(--bottom-nav-height)] items-center justify-around">
        {items.map((item) => {
          const active = location.pathname === item.to || (item.to !== '/m/' && location.pathname.startsWith(item.to));
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={cn(
                'flex h-full flex-1 flex-col items-center justify-center gap-1 text-[10px] font-medium transition-colors',
                active ? 'text-primary' : 'text-muted-foreground'
              )}
              onClick={() => {
                if (navigator.vibrate) navigator.vibrate(10);
              }}
              onTouchStart={() => prefetchRoute(item.to)}
              onMouseEnter={() => prefetchRoute(item.to)}
            >
              <item.icon className={cn('h-5 w-5', active && 'fill-primary/20')} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}

        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerTrigger asChild>
            <button
              className={cn(
                'flex h-full flex-1 flex-col items-center justify-center gap-1 text-[10px] font-medium transition-colors',
                moreActive ? 'text-primary' : 'text-muted-foreground'
              )}
              onClick={() => {
                if (navigator.vibrate) navigator.vibrate(10);
              }}
            >
              <MoreHorizontal className={cn('h-5 w-5', moreActive && 'fill-primary/20')} />
              <span>Mais</span>
            </button>
          </DrawerTrigger>

          <DrawerContent className="pb-[max(env(safe-area-inset-bottom),0.5rem)]">
            <DrawerHeader>
              <DrawerTitle>Mais opcoes</DrawerTitle>
            </DrawerHeader>
            <div className="grid gap-2 px-4 pb-4">
              <Button variant="outline" className="justify-start gap-2" onClick={() => { setDrawerOpen(false); navigate('/m/tasks'); }}>
                <CheckSquare className="h-4 w-4" /> Tarefas
              </Button>
              <Button variant="outline" className="justify-start gap-2" onClick={() => { setDrawerOpen(false); navigate('/m/calls'); }}>
                <Phone className="h-4 w-4" /> Chamadas
              </Button>
              <Button variant="outline" className="justify-start gap-2" onClick={() => { setDrawerOpen(false); navigate('/m/profile'); }}>
                <UserCircle className="h-4 w-4" /> Perfil
              </Button>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </nav>,
    document.body
  );
}
